// Runtime configuration injected by /runtime-config.js (loaded before the bundle).
// CI writes the per-env values at deploy time — see scripts/deploy-frontend.sh.

export interface RuntimeConfig {
  bffBaseUrl: string;
  apiBaseUrl: string;
  environment: string;
  gitSha: string;
}

declare global {
  interface Window {
    __FINANCE_CONFIG__: RuntimeConfig;
  }
}

const cfg: RuntimeConfig = window.__FINANCE_CONFIG__ ?? {
  bffBaseUrl: 'http://localhost:8080',
  apiBaseUrl: 'http://localhost:8080',
  environment: 'unknown',
  gitSha: 'unknown',
};

export const ENV = cfg.environment;
export const GIT_SHA = cfg.gitSha;

export interface HealthResponse {
  message: string;
  env: string;
  ts: number;
  items_table: string;
  git_sha?: string;
}

export async function getHealth(): Promise<HealthResponse> {
  return apiFetch<HealthResponse>('/health');
}

// ----- Auth + accounts -----

export interface User {
  id: string;
  email: string;
  name: string;
}

export interface Account {
  id: string;
  name: string;
  role: string;
}

export interface MeResponse {
  user: User;
  accounts: Account[];
}

export async function register(input: {
  email: string;
  password: string;
  name: string;
  account_name?: string;
}): Promise<{ user: User; account: Account }> {
  return apiFetch('/auth/register', { method: 'POST', body: JSON.stringify(input) });
}

export async function login(email: string, password: string): Promise<{ user: User }> {
  return apiFetch('/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
}

export async function logout(): Promise<void> {
  await apiFetch('/auth/logout', { method: 'POST' });
}

export async function getMe(): Promise<MeResponse> {
  return apiFetch<MeResponse>('/auth/me');
}

export async function listAccounts(): Promise<{ accounts: Account[] }> {
  return apiFetch('/accounts');
}

export async function createAccount(name: string): Promise<Account> {
  return apiFetch('/accounts', { method: 'POST', body: JSON.stringify({ name }) });
}

export async function inviteToAccount(accountId: string, email: string): Promise<{ ok: boolean }> {
  return apiFetch(`/accounts/${encodeURIComponent(accountId)}/invite`, {
    method: 'POST',
    body: JSON.stringify({ email }),
  });
}

// ----- Funds -----

export interface Fund {
  id: string;
  sedol: string;
  isin: string;
  name: string;
  category: string;
  benchmark: string;
  ongoing_charge_pct: number;
  performance_fee_pct: number;
  initial_charge_pct: number;
  inception_date: string;
  manager_name: string;
  source_url: string;
  morningstar_sec_id?: string;
  created_at: string;
  updated_at: string;
}

export interface AllocationBucketRow {
  bucket: string;
  pct: number;
}

export interface HoldingRow {
  name: string;
  sector: string;
}

export interface TrailingReturnRow {
  period: string;
  fund: number;
  category: number;
  benchmark: number;
}

export interface CalendarReturnRow {
  year: number;
  fund: number;
  category: number;
  benchmark: number;
}

export interface FundPriceSnapshot {
  fund_id: string;
  date: string;
  nav_pence: number;
  currency?: string;
  day_change_pct: number;
}

export interface FundAllocation {
  fund_id: string;
  date: string;
  yield_pct: number;
  tax_year_return_pct: number;
  total_net_assets_mil: number;
  top_regions: AllocationBucketRow[];
  top_sectors: AllocationBucketRow[];
  top_holdings: HoldingRow[];
  trailing_returns: TrailingReturnRow[];
  calendar_returns: CalendarReturnRow[];
}

export interface FundDetail {
  fund: Fund;
  latest_price: FundPriceSnapshot | null;
  latest_allocation: FundAllocation | null;
}

export interface UploadFundResponse {
  fund: Fund;
  latest_price: FundPriceSnapshot | null;
  latest_allocation: FundAllocation | null;
}

export async function listFunds(accountId: string): Promise<{ funds: Fund[] }> {
  return apiFetch(`/accounts/${encodeURIComponent(accountId)}/funds`);
}

export async function getFund(accountId: string, id: string): Promise<FundDetail> {
  return apiFetch(`/accounts/${encodeURIComponent(accountId)}/funds/${encodeURIComponent(id)}`);
}

// uploadFundPdf POSTs the raw PDF body to a specific account. Override
// Content-Type to application/pdf so the API Gateway HTTP API treats it
// as binary (and the lambda receives the raw bytes, base64-decoded
// automatically).
export async function uploadFundPdf(accountId: string, file: File): Promise<UploadFundResponse> {
  return apiFetch(`/accounts/${encodeURIComponent(accountId)}/funds`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/pdf' },
    body: file,
  });
}

// addFundByISIN creates/refreshes a fund using Morningstar's public JSON
// endpoints — no PDF needed. Returns the same shape as PDF upload but
// without an allocation snapshot (the screener row doesn't carry it).
export async function addFundByISIN(accountId: string, isin: string): Promise<UploadFundResponse> {
  return apiFetch(`/accounts/${encodeURIComponent(accountId)}/funds/by-isin`, {
    method: 'POST',
    body: JSON.stringify({ isin }),
  });
}

// addCryptoFund creates/refreshes a Fund (provider=coingecko) backed by
// CoinGecko's public REST API. coinId is CoinGecko's slug, e.g. "bitcoin".
export async function addCryptoFund(
  accountId: string,
  coinId: string
): Promise<UploadFundResponse> {
  return apiFetch(`/accounts/${encodeURIComponent(accountId)}/funds/coin`, {
    method: 'POST',
    body: JSON.stringify({ coin_id: coinId }),
  });
}

// refreshFund re-pulls the latest NAV for a single fund from Morningstar
// and writes a new price snapshot. Cheap to call on demand.
export async function refreshFund(accountId: string, fundId: string): Promise<UploadFundResponse> {
  return apiFetch(
    `/accounts/${encodeURIComponent(accountId)}/funds/${encodeURIComponent(fundId)}/refresh`,
    { method: 'POST' }
  );
}

// listFundPrices returns the daily NAV history for one fund, oldest first.
// Optional `since` (YYYY-MM-DD) trims the response to a recent window.
export async function listFundPrices(
  accountId: string,
  fundId: string,
  since?: string
): Promise<{ prices: FundPriceSnapshot[] }> {
  const qs = since ? `?since=${encodeURIComponent(since)}` : '';
  return apiFetch(
    `/accounts/${encodeURIComponent(accountId)}/funds/${encodeURIComponent(fundId)}/prices${qs}`
  );
}

// ----- Targets + Dashboard -----

export type TargetMap = Record<string, number>;

export interface Targets {
  wealth: TargetMap;
  asset: TargetMap;
  geo: TargetMap;
  sector: TargetMap;
  risk: TargetMap;
  updated_at?: string;
}

export interface DashboardBucket {
  label: string;
  current_pct: number;
  desired_pct: number;
}

export interface UnknownFund {
  fund_id: string;
  fund_name: string;
  value_pence: number;
}

export interface DashboardDimension {
  buckets: DashboardBucket[];
  coverage_pct: number;
  unknown_funds?: UnknownFund[];
}

export interface Dashboard {
  total_value_pence: number;
  wealth: DashboardDimension;
  asset: DashboardDimension;
  geo: DashboardDimension;
  sector: DashboardDimension;
  risk: DashboardDimension;
}

export async function getTargets(accountId: string): Promise<Targets> {
  return apiFetch(`/accounts/${encodeURIComponent(accountId)}/targets`);
}

export async function updateTargets(accountId: string, t: Targets): Promise<Targets> {
  return apiFetch(`/accounts/${encodeURIComponent(accountId)}/targets`, {
    method: 'PUT',
    body: JSON.stringify(t),
  });
}

export async function getDashboard(accountId: string): Promise<Dashboard> {
  return apiFetch(`/accounts/${encodeURIComponent(accountId)}/dashboard`);
}

// ----- Projection inputs (account-level retirement-projection knobs) -----

export interface ProjectionInputs {
  real_return_pct: number;
  years_to_retire: number;
  swr_pct: number;
  include_downsize: boolean;
  replacement_home_pence: number;
  updated_at?: string;
}

export async function getProjectionInputs(accountId: string): Promise<ProjectionInputs> {
  return apiFetch(`/accounts/${encodeURIComponent(accountId)}/projection-inputs`);
}

export async function updateProjectionInputs(
  accountId: string,
  p: Omit<ProjectionInputs, 'updated_at'>
): Promise<ProjectionInputs> {
  return apiFetch(`/accounts/${encodeURIComponent(accountId)}/projection-inputs`, {
    method: 'PUT',
    body: JSON.stringify(p),
  });
}

// ----- Vehicles -----

// Vehicle is a tax wrapper (SIPP / ISA / GIA / etc) that groups holdings
// + a GBP cash balance. Names are unique per account. Owner is the
// human the wrapper belongs to (drives the dashboard's Wealth chart).
export interface Vehicle {
  id: string;
  name: string;
  owner: string;
  kind?: string;
  cash_pence: number;
  last_updated: string;
  created_at: string;
  platform_fee_pct_per_year?: number;
  platform_fee_flat_pence?: number;
  advisor_fee_pct_per_year?: number;
  planned_annual_contribution_pence?: number;
  contributions?: Contribution[];
}

export interface Contribution {
  id: string;
  date: string;
  amount_pence: number;
  source: 'personal' | 'employer' | string;
  note?: string;
}

export async function listVehicles(accountId: string): Promise<{ vehicles: Vehicle[] }> {
  return apiFetch(`/accounts/${encodeURIComponent(accountId)}/vehicles`);
}

export async function createVehicle(
  accountId: string,
  input: { name: string; owner: string; kind?: string; cash_pence: number }
): Promise<Vehicle> {
  return apiFetch(`/accounts/${encodeURIComponent(accountId)}/vehicles`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function updateVehicle(
  accountId: string,
  vehicleId: string,
  input: {
    name: string;
    owner: string;
    kind?: string;
    cash_pence: number;
    platform_fee_pct_per_year?: number;
    platform_fee_flat_pence?: number;
    advisor_fee_pct_per_year?: number;
    planned_annual_contribution_pence?: number;
    contributions?: Contribution[];
  }
): Promise<Vehicle> {
  return apiFetch(
    `/accounts/${encodeURIComponent(accountId)}/vehicles/${encodeURIComponent(vehicleId)}`,
    {
      method: 'PUT',
      body: JSON.stringify(input),
    }
  );
}

export async function deleteVehicle(accountId: string, vehicleId: string): Promise<void> {
  await apiFetch(
    `/accounts/${encodeURIComponent(accountId)}/vehicles/${encodeURIComponent(vehicleId)}`,
    {
      method: 'DELETE',
    }
  );
}

// ----- Properties -----

// Property is a real-estate asset (residential, BTL, holiday let). Unlike
// a Fund holding, it has no quantity or daily price feed: current_value
// is updated manually whenever the user gets a new valuation. Mortgage
// balance is optional and tracked so future net-worth views can subtract.
//
// Ownership: `owners` is the structured share split (sums to 100).
// `owner` (singular) is preserved for back-compat; new code should
// read `owners` and treat an empty array with a non-empty `owner` as
// a single 100% share.
export interface PropertyOwner {
  name: string;
  share_pct: number;
}

export interface Property {
  id: string;
  name: string;
  owner: string;
  owners?: PropertyOwner[];
  kind?: string;
  address?: string;
  purchase_price_pence: number;
  purchase_date?: string;
  current_value_pence: number;
  value_as_of?: string;
  mortgage_balance_pence: number;
  mortgage_interest_rate_pct?: number;
  mortgage_monthly_payment_pence?: number;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export interface PropertyInput {
  name: string;
  owner?: string;
  owners?: PropertyOwner[];
  kind?: string;
  address?: string;
  purchase_price_pence: number;
  purchase_date?: string;
  current_value_pence: number;
  value_as_of?: string;
  mortgage_balance_pence: number;
  mortgage_interest_rate_pct?: number;
  mortgage_monthly_payment_pence?: number;
  notes?: string;
}

export async function listProperties(accountId: string): Promise<{ properties: Property[] }> {
  return apiFetch(`/accounts/${encodeURIComponent(accountId)}/properties`);
}

export async function createProperty(accountId: string, input: PropertyInput): Promise<Property> {
  return apiFetch(`/accounts/${encodeURIComponent(accountId)}/properties`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function updateProperty(
  accountId: string,
  propertyId: string,
  input: PropertyInput
): Promise<Property> {
  return apiFetch(
    `/accounts/${encodeURIComponent(accountId)}/properties/${encodeURIComponent(propertyId)}`,
    {
      method: 'PUT',
      body: JSON.stringify(input),
    }
  );
}

export async function deleteProperty(accountId: string, propertyId: string): Promise<void> {
  await apiFetch(
    `/accounts/${encodeURIComponent(accountId)}/properties/${encodeURIComponent(propertyId)}`,
    {
      method: 'DELETE',
    }
  );
}

// ----- Chat (Bedrock Claude + Serper web search) -----

export interface ChatAttachment {
  // Filename. Backend sanitises before passing to Bedrock — only ASCII
  // letters/digits/spaces/hyphens/parens/brackets survive on the wire.
  name: string;
  // One of: 'pdf' | 'csv' | 'doc' | 'docx' | 'xls' | 'xlsx' | 'html' | 'txt' | 'md'.
  // Anything else gets a 400 from the lambda.
  format: string;
  // Base64-encoded file bytes. Single-attachment cap is 3 MiB raw
  // (~4 MiB on the wire) — Bedrock's per-request payload ceiling is
  // ~4.5 MiB and we replay history every turn.
  data: string;
}

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  attachments?: ChatAttachment[];
}

export interface ChatSearchHit {
  title: string;
  link: string;
  snippet: string;
}

export interface ChatSearchedItem {
  query: string;
  hits: ChatSearchHit[];
}

export interface ChatResponse {
  reply: string;
  searches_used?: ChatSearchedItem[];
  input_tokens: number;
  output_tokens: number;
  cache_read_tokens?: number;
}

// sendChatMessage POSTs the full conversation (history + current user
// turn as the last entry) and returns Claude's reply plus any web
// searches it ran. Conversation state lives in the component, not on
// the server — pass everything every call.
export async function sendChatMessage(
  accountId: string,
  messages: ChatMessage[]
): Promise<ChatResponse> {
  return apiFetch(`/accounts/${encodeURIComponent(accountId)}/chat`, {
    method: 'POST',
    body: JSON.stringify({ messages }),
  });
}

// ----- Vehicle import (paste from broker statement) -----

export interface ImportPreviewMatch {
  sec_id: string;
  isin?: string;
  name: string;
  category?: string;
  currency?: string;
  latest_nav_pence?: number;
}

export interface ImportPreviewRow {
  name: string;
  type?: string;
  market_value_pence: number;
  portfolio_pct?: number;
  asset_class?: string;
  sector?: string;
  matches: ImportPreviewMatch[];
}

export interface ImportPreviewResponse {
  rows: ImportPreviewRow[];
}

export interface ImportResultRow {
  isin: string;
  status: 'created' | 'updated' | 'skipped';
  fund_id?: string;
  holding_id?: string;
  reason?: string;
  quantity?: number;
}

export interface ImportResponse {
  created: number;
  updated: number;
  skipped: number;
  results: ImportResultRow[];
}

export async function previewVehicleImport(
  accountId: string,
  vehicleId: string,
  paste: string
): Promise<ImportPreviewResponse> {
  return apiFetch(
    `/accounts/${encodeURIComponent(accountId)}/vehicles/${encodeURIComponent(vehicleId)}/import-preview`,
    { method: 'POST', body: JSON.stringify({ paste }) }
  );
}

export async function confirmVehicleImport(
  accountId: string,
  vehicleId: string,
  items: { isin: string; market_value_pence: number }[],
  openedAt?: string
): Promise<ImportResponse> {
  return apiFetch(
    `/accounts/${encodeURIComponent(accountId)}/vehicles/${encodeURIComponent(vehicleId)}/import`,
    { method: 'POST', body: JSON.stringify({ items, opened_at: openedAt || undefined }) }
  );
}

// ----- Holdings (a.k.a. "Investments" in the UI) -----

// Holding is the raw row written to the table — what create + update return.
// Owner now lives on the parent Vehicle, not on the holding.
export interface Holding {
  id: string;
  fund_id: string;
  vehicle_id?: string;
  quantity: number;
  book_cost_pence: number;
  provider?: string;
  platform?: string;
  opened_at?: string;
  last_updated: string;
  created_at: string;
}

// EnrichedHolding is what the list endpoint returns — joined with the fund
// name + latest NAV + vehicle name + vehicle owner. Market value/gain-loss/
// current price are nullable because a holding's fund might not have a
// price snapshot yet (PDF missing or NAV failed to parse).
export interface EnrichedHolding extends Holding {
  fund_name: string;
  fund_ocf_pct?: number;
  owner: string;
  vehicle_name?: string;
  current_price_pence?: number;
  price_currency?: string;
  market_value_pence?: number;
  gain_loss_pence?: number;
  price_as_of?: string;
  // nav_at_open_pence is the fund's NAV on (or just after) opened_at —
  // anchors the annualised-return calc displayed alongside Gain/Loss.
  nav_at_open_pence?: number;
  nav_at_open_as_of?: string;
  growth_pct_per_year?: number;
}

export async function listHoldings(accountId: string): Promise<{ holdings: EnrichedHolding[] }> {
  return apiFetch(`/accounts/${encodeURIComponent(accountId)}/holdings`);
}

export async function createHolding(
  accountId: string,
  input: {
    fund_id: string;
    vehicle_id: string;
    quantity: number;
    book_cost_pence: number;
    provider?: string;
    platform?: string;
    opened_at?: string;
  }
): Promise<Holding> {
  return apiFetch(`/accounts/${encodeURIComponent(accountId)}/holdings`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

export async function updateHolding(
  accountId: string,
  holdingId: string,
  input: {
    vehicle_id: string;
    quantity: number;
    book_cost_pence: number;
    provider?: string;
    platform?: string;
    opened_at?: string;
  }
): Promise<Holding> {
  return apiFetch(
    `/accounts/${encodeURIComponent(accountId)}/holdings/${encodeURIComponent(holdingId)}`,
    {
      method: 'PUT',
      body: JSON.stringify(input),
    }
  );
}

export async function deleteHolding(accountId: string, holdingId: string): Promise<void> {
  await apiFetch(
    `/accounts/${encodeURIComponent(accountId)}/holdings/${encodeURIComponent(holdingId)}`,
    {
      method: 'DELETE',
    }
  );
}

// ApiError is thrown for non-2xx responses with the server's error message.
export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${cfg.bffBaseUrl}${path}`, {
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(init.headers ?? {}),
    },
    ...init,
  });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const body = await res.json();
      if (body && typeof body.error === 'string') msg = body.error;
    } catch {
      // ignore — non-JSON body
    }
    throw new ApiError(res.status, msg);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}
